import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { hasPlatformCapability, normalizePlatformAccess } from "../lib/platformAccess";

const PlatformAccessContext = createContext(null);

export function PlatformAccessProvider({ children }) {
  const [state, setState] = useState({ status: "loading", access: null, error: null });
  const requestRef = useRef(null);

  const loadAccess = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState({ status: "loading", access: null, error: null });

    try {
      const { data } = await api.get("/platform/access", { signal: controller.signal });
      const access = normalizePlatformAccess(data);
      if (access.roles.length === 0) throw new TypeError("No active platform role");
      if (!controller.signal.aborted) {
        setState({ status: "ready", access, error: null });
      }
    } catch (error) {
      if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
      const responseStatus = Number(error?.response?.status || 0);
      setState({
        status: "error",
        access: null,
        error: {
          kind: responseStatus === 401 || responseStatus === 403 ? "denied" : "unavailable",
          status: responseStatus || null,
        },
      });
    }
  }, []);

  useEffect(() => {
    loadAccess();
    return () => requestRef.current?.abort();
  }, [loadAccess]);

  const hasCapability = useCallback(
    (capability) => hasPlatformCapability(state.access, capability),
    [state.access]
  );

  const value = useMemo(
    () => ({ ...state, retry: loadAccess, hasCapability }),
    [state, loadAccess, hasCapability]
  );

  return (
    <PlatformAccessContext.Provider value={value}>
      {children}
    </PlatformAccessContext.Provider>
  );
}

export function usePlatformAccess() {
  const value = useContext(PlatformAccessContext);
  if (!value) throw new Error("usePlatformAccess must be used within PlatformAccessProvider");
  return value;
}
